import type { Equipment } from '../../../api/active/generated/equipment';
import type { QuoteFieldDefinition } from '../../../api/active/generated/settings';
import type { DeviceCatalogRecord, QuoteItem } from './types';

type EquipmentCustomField = NonNullable<Equipment['customFields']>[number];
export type QuoteDeviceCustomField = {
  -readonly [Key in keyof EquipmentCustomField]: EquipmentCustomField[Key];
};

/** Preserve the exact typed binding. Preview capabilities never enter a frozen value. */
export function quoteDeviceCustomFields(
  value: unknown,
): QuoteDeviceCustomField[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (
      !entry ||
      typeof entry !== 'object' ||
      typeof entry.definitionId !== 'string' ||
      !Number.isSafeInteger(entry.definitionVersion) ||
      entry.definitionVersion < 1
    )
      return [];
    const raw = entry.value;
    let frozen: QuoteDeviceCustomField['value'];
    if (
      raw === null ||
      typeof raw === 'string' ||
      (typeof raw === 'number' && Number.isFinite(raw))
    )
      frozen = raw;
    else if (
      raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      typeof raw.storedObjectId === 'string' &&
      ['image/jpeg', 'image/png', 'image/webp'].includes(raw.mediaType) &&
      Number.isSafeInteger(raw.byteSize) &&
      raw.byteSize >= 0 &&
      typeof raw.isPrimary === 'boolean' &&
      Number.isInteger(raw.sortOrder)
    ) {
      frozen = {
        storedObjectId: raw.storedObjectId,
        mediaType: raw.mediaType,
        byteSize: raw.byteSize,
        isPrimary: raw.isPrimary,
        sortOrder: raw.sortOrder,
      };
    } else return [];
    const field: QuoteDeviceCustomField = {
      definitionId: entry.definitionId,
      definitionVersion: entry.definitionVersion,
      value: frozen,
    };
    for (const key of [
      'fieldKey',
      'definitionRevisionId',
      'deviceId',
      'deviceRevisionId',
    ] as const)
      if (typeof entry[key] === 'string') field[key] = entry[key];
    if (
      ['text', 'number', 'money', 'single_select', 'image'].includes(
        entry.valueType,
      )
    )
      field.valueType = entry.valueType;
    if (entry.options === null) field.options = null;
    else if (Array.isArray(entry.options))
      field.options = entry.options.flatMap(
        (option: Record<string, unknown>) => {
          if (
            !option ||
            typeof option.value !== 'string' ||
            typeof option.displayLabel !== 'string' ||
            typeof option.enabled !== 'boolean' ||
            !Number.isInteger(option.sortOrder)
          )
            return [];
          return [
            {
              value: option.value,
              displayLabel: option.displayLabel,
              enabled: option.enabled,
              sortOrder: option.sortOrder as number,
            },
          ];
        },
      );
    return [field];
  });
}

export function resolveQuoteDeviceCustomField(
  item: QuoteItem,
  key: string,
  definitions: readonly QuoteFieldDefinition[] = [],
): QuoteDeviceCustomField | undefined {
  for (const field of item.deviceFieldSnapshot?.customFields ?? []) {
    if (
      (field.deviceId && field.deviceId !== item.deviceId) ||
      (field.deviceRevisionId &&
        field.deviceRevisionId !== item.deviceFieldSnapshot?.deviceRevisionId)
    )
      continue;
    if (field.fieldKey === key && field.valueType) return field;
    if (field.fieldKey && field.fieldKey !== key) continue;
    const definition = definitions.find(
      (candidate) =>
        candidate.fieldKey === key &&
        candidate.id === field.definitionId &&
        candidate.definitionVersion === field.definitionVersion,
    );
    if (
      !definition ||
      !['text', 'number', 'money', 'single_select', 'image'].includes(
        definition.valueType,
      )
    )
      continue;
    return {
      ...field,
      fieldKey: key,
      valueType: definition.valueType as QuoteDeviceCustomField['valueType'],
      options: definition.options,
    };
  }
  return undefined;
}

export function quoteDeviceCustomFieldValue(
  field: QuoteDeviceCustomField,
): string | number | null {
  const value = field.value;
  if (value === null) return null;
  if (field.valueType === 'money')
    return typeof value === 'number' && Number.isSafeInteger(value)
      ? value / 100
      : null;
  if (field.valueType === 'number')
    return typeof value === 'number' ? value : null;
  if (field.valueType === 'text')
    return typeof value === 'string' ? value : null;
  if (field.valueType === 'single_select' && typeof value === 'string') {
    const option = field.options?.find(
      (candidate) => candidate.value === value,
    );
    return option?.displayLabel ?? null;
  }
  return null;
}

export function quoteDeviceCustomFieldImagePreview(
  item: QuoteItem,
  field: QuoteDeviceCustomField,
  devices: readonly DeviceCatalogRecord[],
): string | undefined {
  if (
    field.valueType !== 'image' ||
    !field.value ||
    typeof field.value !== 'object'
  )
    return undefined;
  const capability = item.formalCustomFieldPreviews?.[field.definitionId];
  if (capability?.storedObjectId === field.value.storedObjectId)
    return capability.url;
  const revisionId = item.deviceFieldSnapshot?.deviceRevisionId;
  if (!revisionId) return undefined;
  const device = devices.find(
    (candidate) =>
      candidate.id === item.deviceId &&
      candidate.currentRevisionId === revisionId,
  );
  const current = device?.customFields?.find(
    (candidate) =>
      candidate.definitionId === field.definitionId &&
      candidate.definitionVersion === field.definitionVersion,
  );
  return current?.value &&
    typeof current.value === 'object' &&
    current.value.storedObjectId === field.value.storedObjectId
    ? device?.customFieldPreviews?.[field.definitionId]
    : undefined;
}
